import { Box, Collapse, IconButton, Paper, Typography } from "@mui/material"
import { DeleteOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { FC, ReactNode } from "react"
import { useState } from "react"
import { useShallow } from "zustand/shallow"

import type { GoodsReceiptTotals } from "../../types"
import { getPurchaseOrderContainerTrackingNumbers } from "../../utils"
import { GoodsReceiptValueOrMultiple } from "../GoodsReceiptValueOrMultiple"

import { PermissionActionType } from "@/constants/permissions"
import type { Order } from "@/graphql/codegen/graphql"
import { useAppView } from "@/hooks/useAppView"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { CountChip } from "@/screens/WarehouseReceiving/components"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { formatNumber } from "@/utils/number"

interface GoodsReceiptPurchaseOrderPanelProps {
  purchaseOrder: Order
  totals: GoodsReceiptTotals
  hasEstimatedValues?: boolean
  isDefaultExpanded?: boolean
  "data-testid"?: string
  children: ReactNode
  onRemove?: (purchaseOrderId: number) => void
}

export const GoodsReceiptPurchaseOrderPanel: FC<GoodsReceiptPurchaseOrderPanelProps> = ({
  purchaseOrder,
  totals,
  hasEstimatedValues = true,
  isDefaultExpanded = false,
  "data-testid": testId = "purchase-order-panel",
  children,
  onRemove,
}) => {
  const { hasPermissions } = usePermissionManager()
  const { isTabletView } = useAppView()
  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])
  const [isExpanded, setIsExpanded] = useState(isDefaultExpanded)
  const { palletsCount, unitsCount, quantity, uomId } = totals
  const uoms = useUnitOfMeasurementsStore(useShallow((s) => s.units))
  const uom = uoms.find((uom) => uom.id === uomId)

  // Derive labels from hasEstimatedValues flag
  const palletsLabel = hasEstimatedValues ? "Est. Pallets" : "Pallets"
  const unitsLabel = hasEstimatedValues ? "Est. Units" : "Units"
  const totalUomLabel = hasEstimatedValues ? "Est. Total UoM" : "Total UoM"

  const shouldShowDeleteButton = canMaintain && onRemove && !isTabletView
  const ArrowIcon = isExpanded ? KeyboardArrowUpOutlined : KeyboardArrowDownOutlined

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <Paper elevation={0} data-testid={testId}>
      <Paper
        elevation={0}
        variant='outlined'
        className={`flex items-center rounded-md p-6 ${isExpanded ? "border-transparent bg-primary-100" : "border-gray-400 bg-white"}`}
      >
        <Box className='flex w-full items-center justify-between gap-x-4'>
          <Box className='mr-auto flex items-center gap-x-8'>
            <Typography variant='subtitle1' component='div' className='font-medium text-gray-800'>
              PO #: <span data-testid='purchase-order-id'>{purchaseOrder.orderId}</span>
            </Typography>
            <Typography variant='body2' component='div' className='font-medium text-gray-800'>
              Container/Tracking #:{" "}
              <GoodsReceiptValueOrMultiple
                values={getPurchaseOrderContainerTrackingNumbers(purchaseOrder)}
                emptyValue='None'
                data-testid='tracking-number'
              />
            </Typography>
          </Box>

          <Box className='flex flex-row flex-wrap items-center justify-end gap-x-4 gap-y-2'>
            <CountChip label={palletsLabel} value={String(palletsCount)} data-testid='pallets-count' />
            <CountChip label={unitsLabel} value={String(unitsCount)} data-testid='units-count' />
            <CountChip
              label={totalUomLabel}
              value={quantity === null ? "-" : `${formatNumber(quantity)} ${uom?.name ?? ""}`}
              data-testid='total-uom-count'
            />
          </Box>

          {shouldShowDeleteButton && (
            <IconButton size='small' onClick={() => onRemove(purchaseOrder.orderId)} data-testid='delete-button'>
              <DeleteOutlined />
            </IconButton>
          )}

          <IconButton size='small' onClick={handleToggleExpand} data-testid='expand-button'>
            <ArrowIcon className='text-gray-700' />
          </IconButton>
        </Box>
      </Paper>

      <Collapse in={isExpanded}>
        <Box className='p-6'>{children}</Box>
      </Collapse>
    </Paper>
  )
}
